import {Component, Event, EventEmitter, h, Host, Listen, Prop, State, Watch} from "@stencil/core";
import "@bun-ui/icons";
import "@bun-ui/buttons";
import "@bun-ui/modals";
import "@bun-ui/layout";
import "@bun-ui/links";
import "@bun-ui/formatter";
import "@bun-ui/markdown-renderer";
import "@muse/header";
import "@muse/button";
import "@muse/context-menu";
import "@muse/minimal-icon-button";
import "@muse/checkbox";
import "@muse/icon";
import "@muse/dialog";
import "@muse/status-message";
import "@muse/language-switch";
import {
  Actions,
  Features,
  getEndpoints,
  getFeatures,
  getMetaData,
  getPossibleActions,
  getUIState,
  getUserDecisions,
  getUserState,
  MetaData,
  UserDecisions,
  UserState
} from "./domain/app-logic";

import {ConfirmationsPayload} from "./antrag/confirmations/confirmations";
import {fetchI18n, replaceText} from "./common/i18n-util";
import {_debug} from "./common/debugger/debugger";
import {BroadcastMessageType, handleBroadcastMessage, registerBroadcastChannel} from "./utils/broadcast.utils";
import {logoutDownloadAccess} from "./utils/logout.utils";

@Component({
  tag: "lb-frontend",
  styleUrl: "./styles.scss",
  shadow: true,
})
export class LBFrontend {

  @Prop({reflect: true}) index; // the only property which is expected to be given to this component externally
  @Prop({reflect: true}) version = "2.0.0";

  // Following property are filled out from "index" backend response

  @Prop({mutable: true}) submitEndpoint; // rest endpoint, where the form should be submitted to

  @Prop({reflect: true, attribute: "dse-markdown-endpoint", mutable: true}) dseMarkdownEndpoint;
  @Prop({reflect: true, attribute: "i18n-endpoint", mutable: true}) i18nEndpoint;
  @Prop({reflect: true, mutable: true}) i18nMetaEndpoint = "/leistungsbeantragung/api/texte/meta";
  @Prop({reflect: true, attribute: "link-betroffenenrechte-formular"}) linkBetroffenenrechteFormular;

  @Prop({reflect: true, attribute: "pathToLeistungsbeschreibung"}) pathToLeistungsbeschreibung;
  @Prop({mutable: true}) appConfigEndpoint;
  @Prop({mutable: true}) appConfig;

  /*
  There are three sources that define the UI State
  1. the features of the Leistung/Form
  2. the current state of the user
  3. The decisions made by the user
  */

  // 1. Form/Leistung Features
  @Prop({mutable: true}) features: Features = {
    supportsSubmitOffline: false,
    formularType: null,
    inactive: false,
  };

  @Event() lb_loggedout: EventEmitter<any>;
  @Event() logout_required: EventEmitter<any>;
  @Event() dataInitialized: EventEmitter<any>;
  @Event() antragGestellt: EventEmitter<any>;
  @Event() reset_logout_confirmation: EventEmitter<any>;
  @Event() disableLeavePageDialogEventEmitter: EventEmitter<any>;

  broadcastChannel: BroadcastChannel | null = null;
  private sessionTimedOut;
  private reloadLocation = window.location.href;
  @Prop({mutable: true}) logoutForOfflineMode;

  updateFeatures(): Features {
    return getFeatures(this.initialData);
  }

  // 2. User State
  @Prop({mutable: true}) userState: UserState = {
    loggedIn: null,
    accountType: null,
    wrongAccountType: null,
    trustLevelInsufficient: null,
  };

  getInitialUserState(): UserState {
    return getUserState(this.initialData);
  }

  // 3. User Decisions
  // the user can make three decisions in the workflow.
  // - see only preview of the form (this decision is made outside the LB-app)
  //   preview decision is made (temporarily) outside the LB, so it is given as "preview" flag in data
  // - fill out form as "buerger" or as "organisation"
  // - user decides wether to send form online or offline
  @Prop({mutable: true}) userDecisions: UserDecisions = {
    preview: null,
    accountType: null,
    offline: null,
    language: null
  };

  @Watch("userDecisions")
  updateUIState() {
    _debug("updateUIState() watch userDecisions new:", this.userDecisions);
    this.state = getUIState(this.initialData, this.userDecisions, this.userState);
    _debug("updateUIState() this.state now:", this.state);
  }

  updateUserDecisions(update?): UserDecisions {
    let userDecisions: UserDecisions = {
      ...this.userDecisions,
      ...getUserDecisions(this.initialData),
    };

    if (update) {
      userDecisions = {...userDecisions, ...update};
    }

    userDecisions.language = document.documentElement.lang;
    return userDecisions;
  }

  setOfflineDecision(mode) {
    _debug("setOfflineDecision: ", mode);
    if (mode === "offline") {
      // if user is logged in, but decides to fill out offline, trigger logout
      if (this.userState.loggedIn && this.actions.logout) {
        this.logoutForOfflineMode = true;
        this.handleLogout();
      } else {
        this.userDecisions = this.updateUserDecisions({offline: mode === "offline"});
      }
    }
    if (mode === "online") {
      if (
        this.state === "trustlevelInsufficient" ||
        this.state === "wrongAccountType" ||
        this.state === "offlineDecision"
      ) {
        if (this.actions.loginOrganisation && this.actions.loginBuerger) {
          if (this.userDecisions.accountType === "organisation") {
            window.location.href = this.actions.loginOrganisation.href;
          } else {
            window.location.href = this.actions.loginBuerger.href;
          }
        } else if (this.actions.loginOrganisation) {
          window.location.href = this.actions.loginOrganisation.href;
        } else if (this.actions.loginBuerger) {
          window.location.href = this.actions.loginBuerger.href;
        } else if (this.actions.reloginOrganisation && this.actions.reloginBuerger) {
          if (this.userDecisions.accountType === "organisation") {
            this.handleLogout(this.actions.reloginOrganisation.href);
          } else {
            this.handleLogout(this.actions.reloginBuerger.href);
          }
        } else if (this.actions.reloginBuerger) {
          this.handleLogout(this.actions.reloginBuerger.href);
        } else if (this.actions.reloginOrganisation) {
          this.handleLogout(this.actions.reloginOrganisation.href);
        } else if (this.actions.logout) {
          window.location.href = this.actions.logout.href;
        }
      }
    }
  }

  @Prop({mutable: true}) initialData; // store response of initial call which contains actions and endpoints for "Leistung"

  // each time userDecisions or initialData changes, update state, userDecisions
  @Watch("initialData")
  updateState(initialData) {
    _debug("updateState(initialData) entered", initialData);

    if (JSON.stringify(this.userDecisions) !== JSON.stringify(this.updateUserDecisions())) {
      this.userDecisions = this.updateUserDecisions();
    }

    this.metaData = getMetaData(this.initialData);
    this.actions = getPossibleActions(this.initialData);
    this.dataInitialized.emit();

    if (this.actions && this.actions.submitOnline) {
      this.submitEndpoint = this.actions.submitOnline;
    }

    this.setLanguage();

    this.dseMarkdownEndpoint = this.baseurl + initialData.api.dse;
    this.i18nEndpoint = this.baseurl + initialData.api.text;
    this.getDSE();
    this.getI18n();

    this.features = this.updateFeatures();
    this.userState = this.getInitialUserState();
    this.endpoints = getEndpoints(this.initialData);
    this.state = getUIState(this.initialData, this.userDecisions, this.userState);

    if (this.state === "formInactive") {
      this.notBeantragbarMsg = true;
    }

    if (this.initialData && this.initialData.entwurf) {
      this.entwurf = this.initialData.entwurf;
    }

    this.userData = this.initialData.userdata;

    _debug("updateState(initialData) final this.state", this.state);
    // show overlay with preview mode info
    this.previewModal = !!this.userDecisions.preview;

    // get confirmations from ZGA or set default feedback channel
    if (this.initialData.entwurf?.antragsdaten) {
      const {payload, ...confirmations} = this.initialData.entwurf.antragsdaten;
      this.confirmations = confirmations;
    }

    if (this.initialData && this.initialData.api && this.initialData.api.config) {
      this.appConfigEndpoint = this.baseurl + this.initialData.api.config;
    }

    this.formularLanguages = this.initialData.languages;
  }

  @Prop({mutable: true}) metaData: MetaData;
  @Prop({mutable: true}) userData: any;
  @Prop({mutable: true}) endpoints;
  @Prop({mutable: true}) actions: Actions;
  @Prop({mutable: true}) formularLanguages: any;

  @Watch('actions')
  watchActions(newValue, oldValue) {
      _debug('watchActions() triggered %s -> %s oldValue', oldValue);
      _debug('newValue', newValue);
  }

  @State() error: any;

  // the possible states of the UI
  @State() state:
    | "busy"
    | "ready"
    | "loading"
    | "preview"
    | "trustlevelInsufficient"
    | "accountTypeDecision"
    | "offlineDecision"
    | "formInactive"
    | "wrongAccountType"
    | "success" = "loading";

  @Watch("state")
  handleStateChange(state) {
    _debug(state, "handleStateChange() entered with new:", 'info');
   
    // update form visibility on state change
    if (state === "formInactive") {
      _debug("state === 'formInactive', this.finalizedForm:", this.finalizedForm);
      if (this.finalizedForm) {
        document.getElementById("formio").style.display = "block";
      } else {
        document.getElementById("formio").style.display = "none";
      }
    } else {
      document.getElementById("formio").style.display = "none";
    }

    _debug("handleStateChange() old userState:", this.userState);
    const initialUserState = this.getInitialUserState();
    this.userState = (this.userState.loggedIn === null || (this.userState.loggedIn === initialUserState.loggedIn)) ? initialUserState : this.userState;
    _debug("handleStateChange() new userState:", this.userState);

    if (this.state === 'ready' && !this.userState.loggedIn && !this.userDecisions.offline) {
      this.loginConfirmation = true;
    }

    if (this.state === 'success') {
      this.antragGestellt.emit();
    }
  }

    @State() baseurl = "";

    @Prop({ mutable: true }) language = "de";

    setLanguage() {
        this.language = document.documentElement.lang;
    }

    loadScripts(result) {
        const scriptUrl = result.api?.scripts
        _debug('loadScripts() url:', scriptUrl);
        if (scriptUrl) {
          const script = document.createElement('script');
          script.onload = () => {
            //do stuff with the script
            _debug('loadScripts() script loaded...');
            this.initialData = result;
          };
          script.src = scriptUrl;
          document.head.appendChild(script);
        }
    }

    getInitialData() {
      // as attribute/property of this component an "index" is given, pointing to the initial endpoint
      if (this.index) {
        // save the host from url given by the initializing index url for later use in other api calls

        let url;

        if (this.index.indexOf("http") !== -1) {
            url = new URL(this.index);
            this.baseurl = url.protocol + "//" + url.hostname + ":" + url.port;
        } else {
            this.baseurl = "";
        }

        // fetch this endpoint for initial data
        fetch(this.index, {
            method: "GET",
            headers: {
                "X-CSRF-TOKEN": "true",
                // "Prefer" header is only used in local mock environment and will be ignored on production
                // this header triggers different example responses based on the value given
                Prefer: "example=buerger_hoch_hoch", // buerger_zwischengespeichert | buerger_untergeordnet_notLoggedin | org_niedrig_nkb | hybrid_niedrig_elster | buerger_hoch_hoch | buerger_hoch_notLoggedin,
            },
        }).then((resp) => {
            if (resp.ok) {
                resp["json"]().then((result) => {
                    _debug("initialData-Result:", result);
                    if (result.api && result.api.scripts) {
                        this.loadScripts(result);
                    } else {
                        this.initialData = result;
                    }
                });
            } else {
              if (resp.status === 401) {
                window.location.href = window.location.pathname;
              } else {
                console.error("error", resp);
                this.getMetaI18n();
                resp["json"]().then((result) => {
                  this.error = result;
                });
              }
            }
        });
      }
    }

    componentDidLoad() {
      this.getInitialData();
      this.getAppConfig();
      this.handleRefocus();
    }

  // save state and data from form renderer
  // ToDo: wrap form-renderer, confirmations and success in one component

  @State() ageInfo;
  @State() currentFormDataTidy; // formData *without userdata* from current state in renderer
  @State() documentIDs; // formData *without userdata* from current state in renderer

  @Listen('formrendererinitiated') handleFormInit() {
   
    _debug("set data in initiated listener:");
    _debug(this.initialData);
    if (this.initialData?.entwurf?.antragsdaten) {
      const {payload, ...confirmations} = this.initialData.entwurf.antragsdaten;
      this.confirmations = confirmations;      
    }
  }

  @State() disableOverlays;

  @Listen('ageinfo') handleAgeInfoGiven(ev) {
    this.ageInfo = ev.detail;
  };

  @Listen('filesUploaded') handleFilesUploaded(ev) {
    this.documentIDs = ev.detail;   
  };

  @Listen('tidyDataUpdate') handleTidyDataUpdate(ev) {
    this.currentFormDataTidy = ev.detail;
  };

    @Prop({mutable: true}) entwurf;

    getDSE() {
        if (this.dseMarkdownEndpoint) {
            fetch(this.dseMarkdownEndpoint, {
                method: "GET",
                headers: {
                    "X-CSRF-TOKEN": "true",
                    "Prefer": "example=dse"
                },
            }).then((resp) => {
                if (resp.ok) {
                    resp["json"]().then((result) => {
                        this.dse = result.markdown;
                    });
                } else {
                    _debug("Getting DSE failed with: ", resp);
                }
            });
        }
    }

    @Prop({ mutable: true }) i18n;

    getI18n() {
      if(!this.i18n){
        fetchI18n(this.i18nEndpoint)
          .then(res => {
             this.i18n = res;
          });
        }
    }

    getMetaI18n() {
      if(!this.i18n){
        let locale = document.documentElement.lang;
        fetchI18n(this.i18nMetaEndpoint + "?locale=" + locale)
          .then(res => {
            this.i18n = {meta:{[locale]: res}};
          });
        }
    }

    @Listen('form_submitted') handleSubmitted() {
        _debug("handleSubmitted() entered");
        this.state = "success";
    };

    @State() confirmations:ConfirmationsPayload;

    @State() finalizedForm;
    @State() notBeantragbarMsg;
    @Listen('not_beantragbar') handleNotBeantragbar() {
      this.finalizedForm = true;
      this.notBeantragbarMsg = true;
      this.state = "formInactive";
    };

    @State() loginConfirmation;

    @State() dse;
    @State() previewModal;

  login() {
    if (this.metaData.formularType === 'hybrid') {
      if (this.userDecisions.accountType === 'buerger') {
        window.location.href = this.actions.loginBuerger.href;
      } else if (this.userDecisions.accountType === 'organisation') {
        window.location.href = this.actions.loginOrganisation.href
      }
    } else if (this.actions.loginOrganisation) {
      window.location.href = this.actions.loginOrganisation.href;
    } else if (this.actions.loginBuerger) {
      window.location.href = this.actions.loginBuerger.href;
    }
  }


  isMinAgeReached() {
    let result = this.getAge() >= this.metaData?.mindestalter;
    _debug("isMinAgeReached()", result);
    return result;
  }

  getAge() {
    let userBirthday = this.userData?.geburtsdatum?.wert;
    _debug("getAge() userBirthday:", userBirthday);
    if (!userBirthday) {
      return 0;
    }

    const today = new Date();
    const birthDate = new Date(userBirthday);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }

  @Watch("appConfigEndpoint")
  getAppConfig() {
    if (!this.appConfig && this.appConfigEndpoint) {
      fetch(this.appConfigEndpoint, {
        method: "GET",
        headers: {
          "X-CSRF-TOKEN": "true",
          "Prefer": "example=config-with-feature-flags"
        },
      }).then((resp) => {
        if (resp.ok) {
          resp["json"]().then((result) => {
            this.appConfig = result;
            _debug('got appConfig:', this.appConfig);
          });
        }
      });
    }
  }

  renderAntrag() {
      let uuid = Math.floor(Math.random()*10000);
      _debug("renderAntrag() new uuid", uuid)
      let result = <lb-antrag
          baseurl={this.baseurl}
          state={this.state}
          initialData={this.initialData}
          submitEndpoint={this.submitEndpoint}
          actions={this.actions}
          userData={this.userData}
          userDecisions={this.userDecisions}
          metaData={this.metaData}
          endpoints={this.endpoints}
          i18n={this.i18n}
          uuid={uuid}
          language={this.language}
          dse={this.dse}
          appConfig={this.appConfig}
          authNotMatched={this.authNotMatched}
      >
          <slot>
              <span>no formio</span>
          </slot>
      </lb-antrag>

      _debug("renderAntrag() result", result);
      return result;
  }

  private handleRefocus() {
    // window.addEventListener('focus', this.synchronizePageState);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  private handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.synchronizePageState();
    }
  }

  @State() authNotMatched;
  synchronizePageState() {
    fetch("/leistungsbeantragung/api/antragsteller", {
      method: "GET",
      headers: {
        "X-CSRF-TOKEN": "true"
      },
    }).then((resp) => {
      if (resp.ok) {
        resp["json"]().then((result) => {

          const accountData = result;
          _debug('synchronizePageState --> antragstellerData:', accountData);
          _debug('synchronizePageState --> userData:', this.userData);

          if(accountData?.userdata?.trustLevel != this.userData?.trustLevel || accountData?.userdata?.providerName != this.userData?.providerName) {
            _debug('reload page...');
            this.authNotMatched = true;
            setTimeout(() => { window.location.reload() }, 200);
          }
        });
      }
    });
  }

  @State() hasSessionTimedout;

  dismissSessionTimeout= () => {
    this.hasSessionTimedout = false;
    this.disableLeavePageDialogEventEmitter.emit();
    const currentUrl = new URL(window.location.origin + window.location.pathname);
    currentUrl.searchParams.delete("sessionTimedOut", "true");
    window.location.href = currentUrl.href;
  }

  @Watch("appConfig")
  registerLogoutHandler(){
    if (this.appConfig?.featureFlags?.['enable-konto-account-menu']) {
      this.registerBroadcastChannel();
    }
  }

  registerBroadcastChannel() {
    if(this.broadcastChannel){
      return;
    }

    _debug("registerBroadcastChannel...");
    this.broadcastChannel = registerBroadcastChannel();

    this.broadcastChannel.onmessage = event => {
      handleBroadcastMessage(event, BroadcastMessageType.LOGOUT, () => {
        _debug("Broadcast-Event: LOGOUT");
        this.userData = null;

        _debug("logout after session timeout: ", this.sessionTimedOut);

        if(this.sessionTimedOut){
          const currentUrl = new URL(window.location.origin + window.location.pathname);
          currentUrl.searchParams.set("sessionTimedOut", "true");
          this.reloadLocation = currentUrl.href;
        }

        logoutDownloadAccess(this.baseurl + this.actions?.logoutDocumentAccess?.href).then(() => {
          this.completeLogout(this.reloadLocation);
        });
      })

      handleBroadcastMessage(event, BroadcastMessageType.SESSION_TIMEOUT, () => {
        _debug("Broadcast-Event: SESSION_TIMEOUT");
        this.sessionTimedOut = true;
      })
    }
  }

  @Listen('logoutAndRetry')
  handleLogoutAndRetry(){
    _debug("enter 'logoutAndRetry'...");
    this.handleLogout();
  }

  handleLogout(locationForReload = window.location.href){
    _debug("enter 'handleLogout' with: ", locationForReload);
    this.reloadLocation = locationForReload;

    if (this.appConfig?.featureFlags?.['enable-konto-account-menu']) {
      _debug("Send logout event on Konto app...");

      const event = new CustomEvent('kontoLogoutEvent', {
        detail: { message: 'Logging out now!' }
      });
      document.dispatchEvent(event);

    } else {
      _debug("Start logout by gateway...");
      this.gatewayLogoutAndRedirect(locationForReload);
    }
  }

  gatewayLogoutAndRedirect(location) {
    fetch("/gateway/de/logout", {
      method: "POST",
      headers: {"X-CSRF-TOKEN": "true"},
    }).then((resp) => {
      _debug("Logout-Response (BeforeRedirect): ", resp);
      if (resp.ok) {
        this.completeLogout(location);
      }
    });
  }

  completeLogout(location) {
    _debug("completeLogout:  'logoutForOfflineMode' is ", this.logoutForOfflineMode);
    _debug("completeLogout:  'location' is ", location);
    this.lb_loggedout.emit();
    if(!this.logoutForOfflineMode){
      window.location.href = location;
      if (location.includes("#")) {
        window.location.reload();
      }
      return;
    }

    // no reload for offline mode required
    this.userState.loggedIn = false;
    this.userState.accountType = null;
    this.userDecisions = this.updateUserDecisions({offline: true});
    this.logoutForOfflineMode = false; // reset the flag
  }

  render() {
    const searchParams = new URLSearchParams(window.location.search);
    this.hasSessionTimedout = searchParams.has('sessionTimedOut');
    return (
      <Host>
        {this.appConfig?.featureFlags?.['enable-konto-account-menu'] && (
          <p-flash-messages lang={this.language}></p-flash-messages>
        )}

        {this.hasSessionTimedout && !this.userData &&
          <muse-dialog open={this.hasSessionTimedout} dismissable id="sessionTimeout-popup" ondismiss={this.dismissSessionTimeout}>
            <span slot="heading">{this.i18n?.meta?.[this.language]?.session?.sessionExpired}</span>
            <muse-text>{this.i18n?.meta?.[this.language]?.session?.sessionTerminationText}</muse-text>
            <muse-button slot="action" variant="primary" onclick={this.dismissSessionTimeout}>
              {this.i18n?.meta?.[this.language]?.session?.formularReloadBtn}
              {this.disableOverlays = true}
            </muse-button>
          </muse-dialog>
        }

        {_debug("lb-frontend.render() entered with state:", this.state)}

        <m-internal-links visually-hidden label={this.i18n?.meta?.[this.language]?.beantragung?.skiplinkLabel}>
          <a href="#content">{this.i18n?.meta?.[this.language]?.beantragung?.skiplinkContent || 'content'}</a>
          <a
            href="muse-context-menu-trigger-minimal"> {this.i18n?.meta?.[this.language]?.beantragung?.skiplinkNavigation ||
            'main menu'} </a>
        </m-internal-links>
        <section class="header-banner" lang="DE">
          <div>
            <img
              src="data:image/webp;base64,UklGRkQAAABXRUJQVlA4TDcAAAAvf8JfABcwzgKBJJb9lXaJ+Q/awCDbSCf5kq85iEb0P8d//vOf//zv+M9//vOf/z3/+c9//vM/AA=="
              alt="Flagge der Bundesrepublik Deutschland"/>
            <p>Eine offizielle Website der Bundesrepublik Deutschland</p>
          </div>
        </section>
        <muse-header>
          <p-meta-links slot="meta-area"></p-meta-links>
          <muse-language-switch languages={this.formularLanguages} slot="meta-area" data-testid="language-switch"/>

          <a slot="logo" href="/">
            <img src="/leistungsbeantragung/logos/logo_adler_verwaltung_digital3_d50458168b.svg"
                 alt="verwaltung.bund.de"/>
          </a>
          <lv-search-main-ce
            id="lv-search"
            slot="search"
            locale={document.documentElement.lang === 'en' ? 'EN' : 'DE'}
            data-test="lv_search_input"
          ></lv-search-main-ce>

          {this.appConfig?.featureFlags?.['enable-konto-account-menu'] && (
            <k-account-menu slot="menu-area" show-session-timeout-flash-message="false">
              <muse-link data-testid="mein-bereich-link"
                         href={"/portal/" + (this.language?.toUpperCase() === 'DE' ? 'DE' : 'EN') + "/mein-bereich"}>
                {this.i18n?.meta?.[this.language]?.userInfo?.myAreaLinkLabel}
              </muse-link>
            </k-account-menu>
          )}

          {!this.appConfig?.featureFlags?.['enable-konto-account-menu'] && (
            <account-user-menu logoutApproveMessage slot="menu-area"/>
          )}

          <muse-context-menu slot="menu-area" fixed placement="bottom-end">
            <muse-context-menu-trigger-minimal
              id="main-navigation"
              slot="trigger" size="unset" aria-label="main menu">
              <muse-icon name="hamburger" slot="icon"></muse-icon>
            </muse-context-menu-trigger-minimal>
            <p-main-navigation></p-main-navigation>
          </muse-context-menu>
        </muse-header>
        
        <m-content class="main" id="content" tab-index="-1">
          {this.initialData?.metadata?.title && this.initialData?.metadata?.behoerdenName && this.i18n && (
            <div class="form-header">
              <div><strong>{this.initialData.metadata.behoerdenName}</strong></div>
              <c-header>{this.i18n?.formular?.[this.language]?.[this.initialData.metadata.title] ||
                this.initialData.metadata.title}</c-header>
            </div>
          )}

          {this.error && this.i18n && (
            <div>
              <lb-errorview
                slot="content"
                i18n={this.i18n}
                error={this.error}
              />
              <c-button data-testid="error-popup-goto-home-btn"
                        centered-content block large
                        onClick={() => {
                          window.location.href = '/';
                        }}
              >
                <span>{this.i18n?.meta?.[this.language]?.error?.gotoHomeBtnLabel}</span>
              </c-button>
            </div>
          )
          }

          {this.state === 'ready' && this.i18n
            && !this.userData // FIXME BSE-32064 16 year olds no longer get the age info
            && this.metaData?.mindestalter && !this.isMinAgeReached()
            && (
              <span class="age-info">
                <i class="info-icon" aria-hidden="true">!</i>
                  <span data-testid="confirmation-age-info">{replaceText(
                    this.i18n?.meta?.[this.language]?.behoerdenTexte?.ageInformationRequired, 'minAge',
                    this.metaData.mindestalter)}</span>
              </span>
            )
          }

          {this.userDecisions.preview && this.actions.formular && this.i18n && (
            <c-container alert px-2 py-1 flat>
              <c-text>
                <p>
                  <c-markdown-renderer>
                    <div slot="source">
                      {replaceText(this.i18n?.meta?.[this.language]?.beantragung?.previewInfo, 'titel',
                        this.metaData.title)}
                    </div>
                  </c-markdown-renderer>
                  <a
                    href={this.actions.formular?.href}>{this.i18n?.meta?.[this.language]?.beantragung?.previewGotoFormularBtn}</a>
                </p>
              </c-text>
            </c-container>
          )}

          {this.state === "accountTypeDecision" && (
            <lb-accounttype-select
              i18n={this.i18n?.meta?.[this.language]}
              onZurAntragsstellung={(ev) => {
                _debug("ev in zurAntragstellung in account type decision", ev);
                this.userDecisions = this.updateUserDecisions({accountType: ev.detail});
              }}
            />
          )}
          {this.state === "wrongAccountType" && (
            <lb-accounttype-mismatch
              userKontentyp={this.userState.accountType}
              i18n={this.i18n?.meta?.[this.language]}
              formularTyp={this.metaData.formularType}
              kontoAccountMenuActive={this.appConfig?.featureFlags?.['enable-konto-account-menu']}
            />
          )}
          {(this.state === "offlineDecision" ||
            this.state === "trustlevelInsufficient") && (
            <lb-context-switch
              supportsOfflineversand={this.features.supportsSubmitOffline}
              userKontentyp={this.userState.accountType || this.userDecisions.accountType}
              language={this.language}
              formularTyp={this.metaData.formularType}
              requiredTrustLevel={this.metaData.trustLevel}
              requiredTrustLevelName={this.metaData.trustLevel}
              trustlevelNotSufficient={this.userState.trustLevelInsufficient}
              onZurAntragsstellung={(ev) => {
                _debug("ev in zurAntragstellung in offline decision", ev);
                this.setOfflineDecision(ev.detail);
              }}
              i18n={this.i18n?.meta?.[this.language]}
            />
          )}

          {this.state === "loading" && <div class="loading"></div>}

          {/* FIXME BSE-32064 the antrag-block is recreated because age-info and save button are removed */}
          {(this.state === "ready" || this.state === "preview" || this.state === "success") && this.initialData
            && this.metaData && this.endpoints && this.i18n && this.renderAntrag()}

          { // show "Login to insert user data" dialog
            this.loginConfirmation && !this.disableOverlays &&
            <muse-dialog open id="anmelde-popup">
              <c-header type="h2" role="heading" data-testid="anmelde-popup-title">
                {this.i18n?.meta?.[this.language]?.behoerdenTexte?.loginHeadline}
              </c-header>

              <div class="confirm-buttons">
                <c-button data-testid="anmelde-popup-accept-button"
                          primary centered-content block large
                          onClick={() => {
                            this.login()
                          }}
                >
                  <span>{this.i18n?.meta?.[this.language]?.behoerdenTexte?.loginBtnLabel}</span>
                </c-button>
                <br/>

                <c-button data-testid="anmelde-popup-deny-button"
                          centered-content block large
                          onClick={() => {
                            this.loginConfirmation = false;
                          }}
                >
                  <span>{this.i18n?.meta?.[this.language]?.behoerdenTexte?.loginDenyBtnLabel}</span>
                </c-button>

              </div>
            </muse-dialog>
          }

          { // show "this Leistung is not available" dialog
            this.notBeantragbarMsg &&
            <muse-dialog open data-test-id="nicht-beantragbar-dialog">
              <c-header type="h2"
                        role="heading">{this.i18n?.meta?.[this.language]?.beantragung?.formularDeactivatedHeadline}</c-header>
              <c-text>{this.i18n?.meta?.[this.language]?.beantragung?.formularDeactivatedText}</c-text>

              <div class="confirm-buttons">
                {!this.finalizedForm && (
                  <c-button primary centered-content block large
                            onClick={() => {
                              this.notBeantragbarMsg = false;
                              window.location.href = "/";
                            }}
                  >
                    <span>{this.i18n?.meta?.[this.language]?.beantragung?.formularDeactivatedGotoHomeBtn}</span>
                  </c-button>
                )}

                {this.finalizedForm && (
                  <c-button centered-content block large
                            onClick={() => {
                              this.notBeantragbarMsg = false;
                            }}
                  >
                    <span>{this.i18n?.meta?.[this.language]?.beantragung?.formularDeactivatedCloseDialogBtn}</span>
                  </c-button>
                )}
              </div>
            </muse-dialog>
          }

          { // show "this is a preview" dialog
            this.previewModal === true && this.actions.formular &&
            <muse-dialog open>
              <c-header type="legend" role="heading">
                {this.i18n?.meta?.[this.language]?.beantragung?.previewOverlayHeadline}
              </c-header>
              <c-text>
                {this.i18n?.meta?.[this.language]?.beantragung?.previewOverlayText}
              </c-text>

              <div class="confirm-buttons">
                <c-button data-testid="preview-popup-leave-preview-and-login-btn"
                          primary centered-content block large
                          onClick={() => {
                            window.location.href = this.actions.formular?.href;
                          }}
                >
                  <span>{this.i18n?.meta?.[this.language]?.beantragung?.previewOverlayGotoFormularBtn}</span>
                </c-button>
                <br/>
                <c-button data-testid="preview-popup-stay-in-preview-btn"
                          centered-content block large
                          onClick={() => {
                            this.previewModal = false;
                          }}
                >
                  {" "}
                  <span>{this.i18n?.meta?.[this.language]?.beantragung?.previewOverlayCloseOverlayBtn}</span>
                </c-button>
              </div>
            </muse-dialog>
          }

          <formrenderer-flashmessage></formrenderer-flashmessage>

        </m-content>

        {/*<lb-tabmgmt/>*/}

        <div style={{'padding-top': '80px'}}>
          <p-footer/>
        </div>

        <p-cookie-banner></p-cookie-banner>

        {_debug("lb-frontend.render() done")}
      </Host>
    );
  }
}